/**
 * Turning the shipment's documents into the items the documents and image
 * sections render.
 *
 * The split between the two sections is by document type — photos, loading
 * diagrams and map screenshots are images, everything else is a document.
 */

import { describeDocumentType } from "@/Helpers/describeDocumentType";
import type { Document } from "@freightsimple/generated-dashboard-openapi-client/models/Document";
import { DocumentType } from "@freightsimple/generated-dashboard-openapi-client/models/DocumentType";
import moment from "moment";
import type { DocumentsSectionItem } from "@/Shared/Shipment/CustomComponents/ShipmentDocumentsSectionGeneric";

export function viewShipmentsIsImageDocument(
  documentType: DocumentType,
): boolean {
  return (
    documentType === DocumentType.ShipperPhoto ||
    documentType === DocumentType.ClaimsPhoto ||
    documentType === DocumentType.Image ||
    documentType === DocumentType.GoogleMapsScreenshot ||
    documentType === DocumentType.LoadingDiagram
  );
}

export function viewShipmentsToDocumentItem(
  document: Document,
  onDownloadDocument: () => Promise<void>,
  onRemoveDocument: () => Promise<void>,
): DocumentsSectionItem {
  return {
    documentId: document.documentId,
    onDownloadDocument,
    // The server decides what the customer may remove; without its yes there
    // is no remove button at all.
    onRemoveDocument: document.customerCanRemove ? onRemoveDocument : undefined,
    documentDescription: describeDocumentType(document.documentType),
    creationDate: moment(document.createdAt),
    existsYet: true,
    documentType: document.documentType,
  };
}

/**
 * Documents the customer is waiting on still get a row, so the page can say
 * "not yet" rather than saying nothing at all.
 */
export function viewShipmentsWithPlaceholderFor(
  documents: Array<DocumentsSectionItem>,
  documentType: DocumentType,
): Array<DocumentsSectionItem> {
  if (documents.some((d) => d.documentType === documentType)) {
    return documents;
  }

  return [
    ...documents,
    {
      documentId: undefined,
      existsYet: false,
      creationDate: undefined,
      documentDescription: describeDocumentType(documentType),
      onDownloadDocument: async () => {},
      documentType,
    },
  ];
}
